import { useEffect, useRef, useState } from "react";
import { X, Loader2, AlertCircle, CheckCircle2, ShieldAlert } from "lucide-react";
import { api, type Dispute } from "../lib/api";

export const REASON_LABELS: Record<string, string> = {
  not_received: "Item / work not received",
  not_as_described: "Not as described",
  quality: "Quality issue",
  other: "Other",
};
export const disputeStatusChip = (s: string, resolution?: string | null) => {
  const map: Record<string, string> = {
    open: "bg-brand-gold/15 text-[#8a5a00]", responded: "bg-navy-50 text-navy-600",
    unresolved: "bg-brand-gold/15 text-[#8a5a00]",
    resolved: "bg-brand-green/10 text-brand-greendark", cancelled: "bg-line text-mut",
  };
  return <span className={`chip capitalize ${map[s] || "bg-navy-50 text-navy-600"}`}>{resolution === "withdrawn" ? "Withdrawn" : s === "unresolved" ? "Unresolved" : s}</span>;
};

/** Open a dispute on a deal. */
export function DisputeModal({ deal, onClose, onDone }: {
  deal: { dealType: "order" | "contract"; dealId: string; contextLabel?: string };
  onClose: () => void; onDone: (d: Dispute) => void;
}) {
  const [reason, setReason] = useState("not_received");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { const el = dialog.current; el?.showModal(); return () => el?.close(); }, []);

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const res = await api.openDispute({ dealType: deal.dealType, dealId: deal.dealId, reason, message: message.trim() || undefined });
      if (res?.dispute) { setDone(true); onDone(res.dispute); }
      else setErr("Could not open the dispute.");
    } catch (failure: unknown) { setErr(failure instanceof Error ? failure.message : "Could not open the dispute."); }
    finally { setBusy(false); }
  }

  return (
    <dialog ref={dialog} aria-label="Open a dispute" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }} className="w-[calc(100%-2rem)] max-w-md rounded-3xl border-0 bg-white p-0 text-navy shadow-xl backdrop:bg-navy/50">
      <div className="w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold text-navy flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-brand-red" />Open a dispute</h3>
          <button aria-label="Close dispute form" disabled={busy} onClick={() => { if (!busy) onClose(); }} className="btn-ghost h-8 w-8 p-0"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
            <div className="font-bold text-navy">Dispute opened</div>
            <p className="text-sm text-mut mt-1">Payment is held. Start discussing the issue with the other party; either of you can ask Admin for help.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-mut mb-3">Opening a dispute on <span className="font-semibold text-navy">{deal.contextLabel || "this deal"}</span> holds the remaining payment and opens a shared discussion immediately. Either party can ask Admin for help.</p>
            <label className="label">Reason</label>
            <select className="field mb-3" value={reason} onChange={(e) => setReason(e.target.value)}>
              {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="label">What happened?</label>
            <textarea className="field h-24 py-2" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the issue for the other party and admin…" maxLength={2000} />
            {err && <p className="text-sm text-brand-red flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" />{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { if (!busy) onClose(); }} className="btn-outline">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}Open dispute</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}
